import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosResponseHeaders,
} from "axios";

import { API_BASE_URL } from "../constants/env";
import { AppStore } from "../data_sources/store/app";

abstract class Response {}

export class SuccessResponse<T> extends Response {
  readonly header?: Partial<AxiosResponseHeaders>;
  readonly data: T | null;
  constructor(data: T, header?: Partial<AxiosResponseHeaders>) {
    super();
    this.data = data;
    this.header = header;
  }
}
export class ErrorResponse extends Response {
  readonly status?: number;
  readonly error: Error;
  constructor(error: Error, status?: number) {
    super();
    this.error = error;
    this.status = status;
  }
  isCanceled(): boolean {
    return !this.status || !!this.error.message.match(/cancelled/gi);
  }
}
export type RequestResponse<T> = SuccessResponse<T> | ErrorResponse;

export class AbortableRequest<T> {
  readonly response: Promise<AxiosResponse<RequestResponse<T>>>;
  abort: () => void;
  constructor(
    response: Promise<AxiosResponse<RequestResponse<T>>>,
    abort: () => void,
  ) {
    this.response = response;
    this.abort = abort;
  }
}

export const BaseRequestClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1000 * 30,
});
BaseRequestClient.interceptors.response.use(
  (response) => {
    return {
      ...response,
      data: new SuccessResponse(response.data, response.headers),
    };
  },
  async (error: AxiosError) => {
    if (axios.isCancel(error)) {
      return {
        ...error.response,
        data: new ErrorResponse(new Error("Request cancelled")),
      };
    }
    if (error.response) {
      AppStore.instance.actions.notification(
        error.response.status,
        error.message,
        3000,
        error.response.status !== 401,
      );
      return {
        ...error.response,
        data: new ErrorResponse(
          new Error(
            error.response.data
              ? JSON.stringify(error.response.data)
              : `Request failed with status code ${error.response.status}`,
          ),
          error.response.status,
        ),
      };
    }
    AppStore.instance.actions.notification(0, error.message, 3000, true);
    return new ErrorResponse(error);
  },
);

export function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): AbortableRequest<T> {
  const controller = new AbortController();
  return new AbortableRequest(
    BaseRequestClient.get<SuccessResponse<T>>(url, {
      ...config,
      signal: controller.signal,
    }),
    () => controller.abort(),
  );
}
export function post<P, T>(
  url: string,
  data?: P,
  config?: AxiosRequestConfig,
): AbortableRequest<T> {
  const controller = new AbortController();
  return new AbortableRequest(
    BaseRequestClient.post<SuccessResponse<T>>(url, data, {
      ...config,
      signal: controller.signal,
    }),
    () => controller.abort(),
  );
}
export function patch<P, T>(
  url: string,
  data?: P,
  config?: AxiosRequestConfig,
): AbortableRequest<T> {
  const controller = new AbortController();
  return new AbortableRequest(
    BaseRequestClient.patch<SuccessResponse<T>>(url, data, {
      ...config,
      signal: controller.signal,
    }),
    () => controller.abort(),
  );
}
export function put<P, T>(
  url: string,
  data?: P,
  config?: AxiosRequestConfig,
): AbortableRequest<T> {
  const controller = new AbortController();
  return new AbortableRequest(
    BaseRequestClient.put<SuccessResponse<T>>(url, data, {
      ...config,
      signal: controller.signal,
    }),
    () => controller.abort(),
  );
}
export function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): AbortableRequest<T> {
  const controller = new AbortController();
  return new AbortableRequest(
    BaseRequestClient.delete<SuccessResponse<T>>(url, {
      ...config,
      signal: controller.signal,
    }),
    () => controller.abort(),
  );
}
